import React, { useState } from 'react';
import './LockFieldModal.css';

export const LOCK_PRESETS = [
  {
    id: 'discovery_call',
    label: 'Discovery Call',
    icon: 'fas fa-phone-alt',
    desc: 'Confirmed on call',
    text: 'Confirmed on call',
  },
  {
    id: 'email_reply',
    label: 'Email Reply',
    icon: 'fas fa-reply',
    desc: 'Verified via email',
    text: 'Verified via email',
  },
  {
    id: 'linkedin_inbox',
    label: 'LinkedIn Message',
    icon: 'fab fa-linkedin-in',
    desc: 'Confirmed on LinkedIn',
    text: 'Confirmed on LinkedIn',
  },
  {
    id: 'company_directory',
    label: 'Company Directory',
    icon: 'fas fa-globe',
    desc: 'Verified on website',
    text: 'Verified on website',
  },
  {
    id: 'meeting_update',
    label: 'Meeting Update',
    icon: 'fas fa-calendar-check',
    desc: 'Updated in meeting',
    text: 'Updated in meeting',
  },
];

const getFieldIcon = (fieldName) => {
  switch (fieldName) {
    case 'email':
      return 'far fa-envelope';
    case 'phone':
      return 'fas fa-phone';
    case 'jobTitle':
      return 'fas fa-briefcase';
    case 'company':
      return 'far fa-building';
    case 'name':
      return 'far fa-user';
    case 'linkedinUrl':
      return 'fab fa-linkedin';
    case 'companyWebsite':
      return 'fas fa-globe';
    default:
      return 'fas fa-tag';
  }
};

const LockFieldModal = ({ isOpen, onClose, fieldName, fieldLabel, currentValue, onConfirm }) => {
  const [selectedPreset, setSelectedPreset] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPreset) {
      setError('Please select a quick verification note.');
      return;
    }

    const finalReason = additionalNotes.trim()
      ? `${selectedPreset} — ${additionalNotes.trim()}`
      : selectedPreset;

    try {
      setLoading(true);
      setError('');
      await onConfirm(fieldName, finalReason, currentValue);
      setSelectedPreset('');
      setAdditionalNotes('');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to lock field');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = (presetText) => {
    if (selectedPreset === presetText) {
      setSelectedPreset('');
    } else {
      setSelectedPreset(presetText);
      setError('');
    }
  };

  const displayTitle = fieldLabel || fieldName || 'Field';

  return (
    <div className="lock-field-modal-overlay" onClick={onClose}>
      <div className="lock-field-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Consistent Modal Header with Styled Icon */}
        <div className="sched-modal-header lock-modal-header">
          <div className="sched-modal-header-left lock-modal-header-left">
            <div className="sched-modal-header-icon lock-modal-header-icon">
              <i className="fas fa-lock" />
            </div>
            <div className="sched-modal-header-info lock-modal-header-info">
              <h2 className="sched-modal-title lock-modal-title">Lock Field: {displayTitle}</h2>
              <p className="sched-modal-subtitle lock-modal-subtitle">
                Protect verified research from automated provider overwrites.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="sched-modal-close-btn lock-modal-close-btn"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="lock-modal-form">
          <div className="lock-modal-body">
            {/* Protective Guidance Callout */}
            <div className="lock-modal-info-banner">
              <div className="lock-banner-icon">
                <i className="fas fa-shield-alt" />
              </div>
              <div className="lock-banner-content">
                <strong>Silent Overwrite Protection:</strong> Automated syncs from Lusha, Apollo,
                and Hunter will be strictly blocked from overwriting this verified data.
              </div>
            </div>

            {/* Target Field Preview Card */}
            <div className="lock-target-card">
              <div className="lock-target-left">
                <div className="lock-target-icon">
                  <i className={getFieldIcon(fieldName)} />
                </div>
                <div className="lock-target-details">
                  <span className="lock-target-label">Current Verified Value</span>
                  <span className="lock-target-value">
                    {currentValue ? (
                      String(currentValue)
                    ) : (
                      <em className="text-slate-400">(No value set)</em>
                    )}
                  </span>
                </div>
              </div>
              <div className="lock-target-status">
                <span className="lock-target-status-dot" /> Ready to Protect
              </div>
            </div>

            {/* Quick Verification Note (2-Column Grid: left right \n left right \n left) */}
            <div className="lock-presets-section">
              <div className="lock-section-label-row">
                <label className="lock-section-label">
                  Quick Verification Note <span className="req-star">*</span>
                </label>
                <span className="lock-badge-hint">Select a template</span>
              </div>
              <div className="lock-presets-grid quick-reasons-tags">
                {LOCK_PRESETS.map((preset) => {
                  const isSelected = selectedPreset === preset.text;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`quick-reason-btn ${isSelected ? 'is-active' : ''}`}
                      onClick={() => handleSelectPreset(preset.text)}
                    >
                      <div className="quick-preset-icon">
                        <i className={preset.icon} />
                      </div>
                      <div className="quick-preset-info">
                        <span className="quick-preset-name">{preset.label}</span>
                        <span className="quick-preset-desc">{preset.desc}</span>
                      </div>
                      <div className="quick-preset-radio">
                        <i className={isSelected ? 'fas fa-check-circle' : 'far fa-circle'} />
                      </div>
                    </button>
                  );
                })}
              </div>
              {error && <p className="lock-modal-error">{error}</p>}
            </div>

            {/* Optional Additional Audit Notes */}
            <div className="lock-textarea-wrap">
              <div className="lock-section-label-row">
                <label htmlFor="lock-additional-notes" className="lock-section-label">
                  Additional Audit Notes
                </label>
                <span className="lock-badge-hint">(Optional)</span>
              </div>
              <textarea
                id="lock-additional-notes"
                rows={2}
                placeholder="Add specific details or leave blank..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Consistent Lead Huntrix Footer */}
          <div className="sched-modal-footer lock-modal-footer">
            <div className="sched-footer-summary lock-footer-summary">
              <i className="fas fa-lock sched-summary-icon lock-summary-icon" />
              <span>
                Target: <strong>{displayTitle}</strong>
              </span>
            </div>
            <div className="sched-footer-actions lock-modal-actions">
              <button
                type="button"
                className="sched-btn-secondary lock-modal-btn lock-modal-btn-cancel"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="sched-btn-primary lock-modal-btn lock-modal-btn-confirm"
                disabled={loading || !selectedPreset}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    <span>Locking...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-lock" />
                    <span>Lock & Protect Field</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LockFieldModal;
